<template>
  <div class="container keyword-attribute">
    <div class="keyword-attribute__title-container">
      <div class="keyword-attribute__title-left">
        <div class="keyword-attribute__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="keyword-attribute__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="keyword-attribute__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="keyword-attribute__title-right"
           v-if="isDataFetched">
        <div class="keyword-attribute__keyword-attributes">
          <span class="keyword-attribute__count">attributes: {{totalItems}}</span>
          <base-select
            class="keyword-attribute__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="keyword-attribute__button" @click="openCreateModal()">
          Create Attribute
        </button>
      </div>
    </div>

    <div class="common-table keyword-attribute__table">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in keywordAttributeTableColumnsConfig"
             :key="column.property"
             :class="{[`keyword-attribute__${column.property}`]: true}">
          <template v-if="column.property === 'edit-button'">
            <!--       Don't show anything       -->
          </template>
          <base-multi-select-sortable-header
            v-else
            class="keyword-attribute__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            :predefinedNumberFilter="'keywords' === column.property ? filterParams[column.property] : null"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="keyword-attribute__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <template v-for="(keywordAttribute) in paginatedKeywords">
          <div :key="keywordAttribute.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in keywordAttributeTableColumnsConfig"
                 :key="column.property"
                 :class="[`keyword-attribute__${column.property}`, `keyword-attribute__row_${column.property}`]">
              <template v-if="column.property === 'edit-button'">
                <a @click="openCreateModal(keywordAttribute.id)" class="edit-icon">
                  <base-material-design-icon icon="edit"/>
                </a>
              </template>
              <template v-else-if="column.property === 'is_active'">
                <base-select class="keywords-attribute__select"
                             :options="activeOptions"
                             borderLess
                             alignLeft
                             shiftLabelRight
                             :selectStyle="{height: '60px!important', width: '75px', marginLeft: '-10px'}"
                             v-model="keywordAttribute.is_active"
                             @change="handleChangeStatus(keywordAttribute)"/>
              </template>
              <template v-else-if="column.property === 'keyword_count'">
                <span @click="keywordCensusLink(keywordAttribute.name)" class="keyword-attribute__attribute_link">{{ keywordAttribute[column.property]?.toLocaleString() }}</span>
              </template>
              <template v-else-if="column.property === 'msv'">
                <span @click="keywordCensusLink(keywordAttribute.name)" class="keyword-attribute__attribute_link">{{ keywordAttribute[column.property]?.toLocaleString() }}</span>
              </template>
              <template v-else>
                  <span>
                    {{ keywordAttribute[column.property] }}
                  </span>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="keyword-attribute__update-loader" v-if="isDataUpdating">
      <base-loader class="keyword-attribute__update-loader-ring"/>
    </div>

    <modal name="create"
           v-bind="changeKeywordAttributeModalProps">
      <CreateUpdateKeywordAttributePopup
        ref="create-keyword-attribute"
        @apply="createOrUpdateKeywordAttribute($event)"
        @delete="deleteKeywordAttribute($event)"
        :selected-keyword-attribute="selectedKeywordAttribute"
        @cancel="closeCreateModal"/>
    </modal>
  </div>
</template>

<script>
import helper from '@/services/Helper'
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import CreateUpdateKeywordAttributePopup from './CreateUpdateKeywordAttributePopup'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'
import attribute from '../../store/modules/attribute'

const ITEMS_PER_PAGE = 100
const EMPTY_FILTERS = {
  name: '',
  is_active: [],
  sort_column: [],
  sort_order: [],
  keywords: {
    operator: null,
    value: null
  },
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}

// table config
const DEFAULT_CONFIG = [
  {
    label: '',
    property: 'edit-button'
  },
  {
    label: 'Name',
    property: 'name',
    isDefaultSort: true // this column will be sorted by default
  },
  {
    label: 'Active',
    property: 'is_active',
    isSmartInput: false,
    options: [
      {
        label: 'Yes',
        value: '1'
      },
      {
        label: 'No',
        value: '0'
      }
    ]
  },
  {
    label: 'Keywords',
    property: 'keyword_count',
    isNumber: true
  },
  {
    label: 'MSV',
    property: 'msv',
    isNumber: true
  }
]
const ACTIVE_OPTIONS = [
  {
    label: 'Yes',
    value: 1
  },
  {
    label: 'No',
    value: 0
  }
]
const PAGINATION_OPTIONS = [
  {
    label: '100',
    value: 100
  },
  {
    label: '250',
    value: 250
  },
  {
    label: '500',
    value: 500
  }
]
export default {
  name: 'KeywordAttributes',
  components: { CreateUpdateKeywordAttributePopup },
  mixins: [BaseSortMixins],
  // filters received form url
  props: ['filters'],
  mounted () {
  },
  data () {
    return {
      activeOptions: [...ACTIVE_OPTIONS],
      isDataFetched: false,
      selectedKeywordAttribute: null,
      isDataUpdating: false,
      sortConfig: [],
      pageItemsLimit: ITEMS_PER_PAGE,
      keywordAttributeTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'name': 'name',
        'is_active': 'is_active'
      },
      paginationOptions: [...PAGINATION_OPTIONS]
    }
  },
  computed: {
    ...mapState('keywordAttributes', ['keywordAttributes']),
    dataMissing () {
      return this.isDataFetched && !this.keywordAttributes.length
    },
    changeKeywordAttributeModalProps: function () {
      const changeKeywordAttributeModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeKeywordAttributeModalProps }
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    },
    totalItems () {
      return this.keywordAttributes.length
    },
    paginatedKeywords () {
      let keywordAttributes = _.cloneDeep(this.keywordAttributes)
      const { page_from = 0, page_to = 100 } = this.filterParams
      return helper.sortByMultipleParams(keywordAttributes, this.sortConfig).slice(page_from, page_to)
    }
  },
  watch: {
    clientId: {
      handler (v) {
        v && this.loadFilteredData()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('keywordAttributes', ['getKeywordCount', 'getKeywordAttributeData', 'createKeywordAttribute', 'deleteKeywordAttributeById', 'updateKeywordAttribute']),
    ...mapActions('keywordCensus', ['postTableView']),
    async loadFilteredData () {
      this.isDataUpdating = true
      await this.getKeywordAttributeData({
        clientId: this.clientId,
        filters: this.filterParams
      })
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    async keywordCensusLink (attribute) {
      const form = {
        'filters': [{
          'field': 'attribute',
          'type': '=',
          'value': attribute
        }]
      }
      const data = await this.postTableView({ form: form, clientId: this.clientId, type: 'keyword_census' })
      const routeData = this.$router.resolve({ path: `/strategy/keyword-census/${data.data}` });
      window.open(routeData.href, '_blank');
    },
    reset () {
      this.sortConfig = []
      this.keywordAttributeTableColumnsConfig = DEFAULT_CONFIG
      this.loadPageData()
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    handleChangeStatus (keywordAttribute) {
      this.createOrUpdateKeywordAttribute(keywordAttribute)
    },
    createOrUpdateKeywordAttribute: async function (keywordAttribute) {
      this.isDataUpdating = true
      try {
        if (keywordAttribute.id && keywordAttribute.id > -1) {
          await this.updateKeywordAttribute({ clientId: this.clientId, keywordAttribute })
          await this.loadFilteredData()
          this.selectedKeywordAttribute = this.keywordAttributes.find(attribute => attribute.id === keywordAttribute.id)
          this.selectedKeywordAttribute.keyword_count = await this.getKeywordCount(keywordAttribute.id)
        } else {
          const newAttribute = await this.createKeywordAttribute({ clientId: this.clientId, keywordAttribute })
          const newAttributeId = newAttribute.attribute_id
          await this.loadFilteredData()
          this.selectedKeywordAttribute = this.keywordAttributes.find(attribute => attribute.id === newAttributeId)
          this.selectedKeywordAttribute.keyword_count = await this.getKeywordCount(newAttributeId)
          this.selectedKeywordAttribute.id = newAttributeId
        }
        this.$refs['create-keyword-attribute'].showErrors([])
      } catch (e) {
        this.$refs['create-keyword-attribute'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    deleteKeywordAttribute: async function (keywordAttributeId) {
      this.isDataUpdating = true
      try {
        await this.deleteKeywordAttributeById({ clientId: this.clientId, keywordAttributeId })
        await this.loadFilteredData()
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-keyword-attribute'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    closeCreateModal () {
      this.selectedKeywordAttribute = null
      this.$modal.hide('create')
    },
    async openCreateModal (keywordAttributeId) {
      this.selectedKeywordAttribute = this.keywordAttributes.find(keywordAttribute => keywordAttribute.id === keywordAttributeId)
      if (keywordAttributeId) {
        this.selectedKeywordAttribute.keyword_count = await this.getKeywordCount(keywordAttributeId)
      }
      this.$modal.show('create')
    },
    handleLimitChange: function (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn: function (event, column) {
      if (column.property === 'keywords') {
        this.filterParams.keywords.value = event.value
        this.filterParams.keywords.operator = event.operator
      } else {
        this.filterParams[column.property] = event
      }
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadFilteredData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
        this.sortConfig[0].sortRecurcive = cell.property === 'is_active'
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property,
          sortRecurcive: cell.property === 'is_active'
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
    },
    cancelSortingAndFilters: function () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
      !this.isDataUpdating && this.loadFilteredData()
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
      this.loadFilteredData()
    }
  }
}
</script>

<style scoped lang="scss">

.common-table {
  border-radius: 4px;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }

    &:last-child {
      padding-right: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.keyword-attribute {
  position: relative;
  min-height: 200vh !important;

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }
  &__attribute_link {
    cursor: pointer;
    color: $rfx-bright-blue !important;

    &:hover {
      text-decoration: underline;
    }
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

    &__copy-button {
      flex: 1 1 50%;
      height: 100%;
      border: none;
      outline: none;
      color: $rfx-dark-march;
      white-space: nowrap;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 90px;
      background-color: #fff;
      font-size: 11px;

      &:hover {
        opacity: 0.5;
      }
    }

    &__clear-filters {
      @extend .keyword-attribute__copy-button;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__keyword-attributes {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &__is_active {
    color: $rfx-dark-march !important;
  }

  &__select {
    flex-basis: 100%;

  }

  &__no-data {
    display: flex;
    height: 300px;
    background-color: #fff;
    border-radius: $rfx-default-border-radius;
    align-items: center;
    justify-content: center;
    font-size: 27px;
    font-weight: 600;
    color: $rfx-medium-grey;
    flex: 1 1;
  }

  &__url {
    flex: 1 0 120px;
    color: $rfx-dark-march;
  }

  &__edit-button {
    flex: 0 0 45px;

    &.common-table__header-cell {
      flex-basis: 0;
    }
  }

  &__name {

    &.common-table__header-cell {
      flex-basis: 3.3%;

      &::v-deep .multi-select__title-container {
        padding-left: 68px;
      }
    }
  }

  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    width: 75px;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: 4px;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}

</style>
